import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import {
  GMB_FALLBACK_AGGREGATE_RATING,
  GMB_FALLBACK_RATING_COUNT,
} from "../constants/gmb";
import {
  fetchAggregatedGmbReviews,
  fetchGmbReviews,
} from "../services/gmbReviews";
import {
  formatDisplayRating,
  pickFeaturedReviews,
} from "../utils/gmbReviewHelpers";
import { isReactSnap } from "../utils/reactSnap";

const GmbReviewsContext = createContext(null);

export function GmbReviewsProvider({ children }) {
  const [aggregateRating, setAggregateRating] = useState(null);
  const [ratingCount, setRatingCount] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isReactSnap()) {
      setAggregateRating(GMB_FALLBACK_AGGREGATE_RATING);
      setRatingCount(GMB_FALLBACK_RATING_COUNT);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;

    async function loadReviews() {
      try {
        const [aggregated, locationReviews] = await Promise.all([
          fetchAggregatedGmbReviews(),
          fetchGmbReviews({ pageSize: "20" }),
        ]);

        if (cancelled) return;

        setAggregateRating(aggregated.aggregateRating);
        setRatingCount(aggregated.ratingCount);
        setReviews(locationReviews);
        setError(null);
      } catch (loadError) {
        if (!cancelled) {
          console.error("Failed to load Google reviews", loadError);
          setAggregateRating(GMB_FALLBACK_AGGREGATE_RATING);
          setRatingCount(GMB_FALLBACK_RATING_COUNT);
          setError(loadError);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadReviews();

    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo(() => {
    const displayRating =
      aggregateRating != null ? formatDisplayRating(aggregateRating) : null;

    return {
      aggregateRating,
      ratingCount,
      reviews,
      featuredReviews: pickFeaturedReviews(reviews, 3),
      displayRating,
      loading,
      error,
      hasData: displayRating != null && ratingCount != null,
    };
  }, [aggregateRating, ratingCount, reviews, loading, error]);

  return (
    <GmbReviewsContext.Provider value={value}>
      {children}
    </GmbReviewsContext.Provider>
  );
}

export function useGmbReviews() {
  const context = useContext(GmbReviewsContext);

  if (!context) {
    throw new Error("useGmbReviews must be used within GmbReviewsProvider");
  }

  return context;
}
